import {
    forwardRef,
    HStack,
    IconButton,
    Text,
    Box,
    Tooltip,
    useDisclosure,
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ResetSfIdModal } from '../ResetSfIdModal'

import { UserIcon } from '@/assets/icon'
import { DeleteIcon, FacilitySFURLIcon } from '@/assets/icon/index.jsx'
import { ExternalLink } from '@/components/Elements/index.js'
import {
    useGetMergedJobSeekerQuery,
    useSelectJobSeeker,
    useSelectedJobSeeker,
    useClearJobSeeker,
} from '@/features/jobseekers'
import { SF_ACCOUNT_URL } from '@/config'
import { getDisplayNameByLength } from '@/features/jobseekers/utils/displayNameByLength'
import { useLazyListTicketsQuery } from '../../api'

export const JobSeekerHeader = forwardRef((props, ref) => {
    const { isOpen, onClose, onOpen } = useDisclosure()
    const navigate = useNavigate()
    const clearJobSeeker = useClearJobSeeker()
    const onReset = useCallback(async () => {
        await clearJobSeeker()
        navigate('/')
        onClose()
    }, [clearJobSeeker, navigate, onClose])
    return (
        <HStack ref={ref} {...props} alignItems="center" w="100%" spacing="6px">
            <Content onClear={onOpen} />
            <ResetSfIdModal
                onReset={onReset}
                onCancel={onClose}
                isOpen={isOpen}
            />
        </HStack>
    )
})

const JobSeekerInformationButton = (props) => {
    return <IconButton boxShadow="none" minW={0} minH={0} {...props} />
}

const JobSeekerInformationText = ({ jobSeeker }) => {
    if (
        jobSeeker.prefecture ||
        jobSeeker.age ||
        jobSeeker.sex ||
        jobSeeker.qualifications
    ) {
        return (
            <>
                {jobSeeker.prefecture && <Text>{jobSeeker.prefecture}</Text>}
                {jobSeeker.age && (
                    <Text>{jobSeeker.age && `${jobSeeker.age}歳`}</Text>
                )}
                {jobSeeker.sex && <Text>{jobSeeker.sex}</Text>}
                {jobSeeker.qualifications && (
                    <Text pl={3}>{jobSeeker.qualifications}</Text>
                )}
            </>
        )
    } else {
        return <Text color="tryt.gray-4">候補者情報なし</Text>
    }
}
JobSeekerInformationText.propTypes = {
    jobSeeker: PropTypes.shape({
        age: PropTypes.number,
        prefecture: PropTypes.string,
        sex: PropTypes.string,
        qualifications: PropTypes.string,
    }),
}

const Content = ({ onClear }) => {
    const jobSeeker = useSelectedJobSeeker()
    const selectJobSeeker = useSelectJobSeeker()

    // クエリストリングから求職者を設定
    const location = useLocation()
    const queryJobseekerSfId = new URLSearchParams(location.search).get(
        'jobseeker-sfid'
    )

    const [trigger] = useLazyListTicketsQuery()

    const { data } = useGetMergedJobSeekerQuery(queryJobseekerSfId, {
        skip: !queryJobseekerSfId || queryJobseekerSfId === jobSeeker?.sfId,
    })

    const startFetch = useCallback(() => {
        if (!data?.sfId) {
            return Promise.resolve()
        }
        //  パラメータはなしですべてのチケットを取得し、フロント側でフィルタリング
        return trigger()
            .unwrap()
            .then((result) => {
                if (!result?.items) {
                    return
                }

                // 同一 jobseekerSfIdのチケットから、distributedAt が最新のものを選択
                const relatedTickets = result.items.filter(
                    (t) => t.jobseekerSfId === data.sfId && t.distributedAt
                )

                const currentTicket = relatedTickets.reduce((prev, t) => {
                    if (!prev) return t
                    return new Date(t.distributedAt) > new Date(prev.distributedAt)
                        ? t
                        : prev
                }, null)

                if (!currentTicket || !currentTicket.distributedAt) {
                    return
                }

                const jobSeekerWithTicket = {
                    detail: data,
                    distributedAt: currentTicket.distributedAt
                }
                selectJobSeeker(jobSeekerWithTicket)
            })
    }, [data, trigger, selectJobSeeker])

    useEffect(() => {
        startFetch()
    }, [data, startFetch])

    if (jobSeeker) {
        return (
            <>
                <UserIcon boxSize="20px" />
                {(() => {
                    const name = jobSeeker.name
                    if (!name) return null
                    const { displayName, truncated } = getDisplayNameByLength(name, 20)
                    return truncated ? (
                        <Tooltip label={name} hasArrow placement="bottom">
                            <Text as="span" cursor="help" fontWeight="bold">
                                {displayName}
                            </Text>
                        </Tooltip>
                    ) : (
                        <Text fontWeight="bold">{displayName}</Text>
                    )
                })()}
                <Text as="span" flexShrink={0}>
                    様
                </Text>
                <JobSeekerInformationButton
                    icon={<FacilitySFURLIcon color="#009ddb" boxSize="20px" />}
                    borderColor="#EAEEF4"
                    h="20px"
                    w="20px"
                    ml="2px"
                    as={ExternalLink}
                    href={SF_ACCOUNT_URL + '/' + jobSeeker.code + '/view'}
                />
                <Text>｜</Text>
                <JobSeekerInformationText jobSeeker={jobSeeker} />
                <Box pt="1px" pl={2}>
                    <JobSeekerInformationButton
                        icon={<DeleteIcon boxSize="20px" />}
                        variant="ghost"
                        border="none"
                        h="20px"
                        w="20px"
                        borderRadius="full"
                        onClick={onClear}
                    />
                </Box>
            </>
        )
    }
    return <Text>候補者未選択</Text>
}
Content.propTypes = {
    onClear: PropTypes.func.isRequired,
}
